import actionCreator from '../../shared/decorators/actionCreator';
import enqueueGAEvent from '../../shared/decorators/enqueueGAEvent';
import recordForUndo from '../../shared/decorators/recordForUndo';
import { REDUX_NAMESPACE } from '../constants';
import { CREATIVE_MODES } from '../../../constants';
import { EVENT_CATEGORY } from '../../analytics/constants';

const ACTION_CREATORS = {
  /**
   * Initialize a new creative. If templateId is supplied, it will use that as the basis.
   *
   * @param template
   * @returns {{template: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  newCreative(template = null) {
    return { template };
  },

  /**
   * Initialize a new template. If isGlobal is true, it will do so under the GLOBAL org. (permissions required)
   * @param isGlobal
   * @returns {{isGlobal: boolean}}
   */
  @actionCreator(REDUX_NAMESPACE)
  newTemplate(isGlobal = false) {
    return { isGlobal };
  },

  @actionCreator(REDUX_NAMESPACE)
  openCreative(
    creativeId = null,
    creativeMode = null,
    creativeOrigin = null,
    editorMode = null,
  ) {
    return {
      creativeId,
      creativeMode,
      creativeOrigin,
      editorMode,
    };
  },

  /**
   * Initialize a new creative from a Template with the intention that it will be saved as a new Template
   *
   * @param template
   * @returns {{template: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  importTemplate(template = null) {
    return { template };
  },

  /**
   * Creates a duplicate of the creative
   *
   * @param creative
   * @returns {{creative: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  duplicateCreative(creative = null) {
    return { creative };
  },

  // indicates that duplicating has started
  @actionCreator(REDUX_NAMESPACE)
  startDuplicateCreative() {},

  // indicates that duplicating has completed
  @actionCreator(REDUX_NAMESPACE)
  finishDuplicateCreative() {},

  /**
   * Creates a duplicate of the template
   *
   * @param template
   * @returns {{template: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  duplicateTemplate(template = null) {
    return { template };
  },

  // indicates that duplicating has started
  @actionCreator(REDUX_NAMESPACE)
  startDuplicateTemplate() {},

  // indicates that duplicating has completed
  @actionCreator(REDUX_NAMESPACE)
  finishDuplicateTemplate() {},

  /**
   * Instantiates the Template Picker flow
   */
  @actionCreator(REDUX_NAMESPACE)
  pickTemplate() {},

  /**
   * Resets the Template for import by clearing it
   */
  @actionCreator(REDUX_NAMESPACE)
  resetImportTemplate() {},

  /**
   * Sets the initial or default state of a creative given the following substates
   *
   * @param elementsState
   * @param contentState
   * @param mediaState
   * @param metadataState
   * @param textState
   * @param playbackState
   * @param outputMediaId
   */
  @actionCreator(REDUX_NAMESPACE)
  setCreativeSubstates(
    elementsState = undefined,
    contentState = undefined,
    mediaState = undefined,
    metadataState = undefined,
    textState = undefined,
    playbackState = undefined,
    outputMediaId = undefined,
  ) {
    const creativeState = {};

    if (typeof elementsState !== 'undefined') {
      creativeState.elements = elementsState;
    }

    if (typeof contentState !== 'undefined') {
      creativeState.content = contentState;
    }

    if (typeof mediaState !== 'undefined') {
      creativeState.media = mediaState;
    }

    if (typeof metadataState !== 'undefined') {
      creativeState.metadata = metadataState;
    }

    if (typeof textState !== 'undefined') {
      creativeState.text = textState;
    }

    if (typeof playbackState !== 'undefined') {
      creativeState.playback = playbackState;
    }

    if (typeof outputMediaId !== 'undefined') {
      creativeState.outputMediaId = outputMediaId;
    }

    return creativeState;
  },

  /**
   * Sets the template being used for a creative
   *
   * @param template
   * @param creativeId
   * @returns {{template: *, elementAutoSelectionEnabled: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  setCreativeTemplate(template, creativeId) {
    return {
      creativeId,
      template,
    };
  },

  /**
   * Sets any errors that have occurred while building a creative
   *
   * @param creativeBuildErrors
   * @returns {{creativeBuildErrors: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  setCreativeBuildErrors(creativeBuildErrors) {
    return { creativeBuildErrors };
  },

  /**
   * Sets Creative Height
   *
   * @param height
   * @returns {{height: number}}
   */
  @enqueueGAEvent(
    EVENT_CATEGORY.UI.NAME,
    EVENT_CATEGORY.UI.ACTION.UPDATE_CREATIVE_DIMENSIONS,
  )
  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  setCreativeHeight(height) {
    return { height };
  },

  /**
   * Sets Creative width
   *
   * @param width
   * @returns {{width: number}}
   */
  @enqueueGAEvent(
    EVENT_CATEGORY.UI.NAME,
    EVENT_CATEGORY.UI.ACTION.UPDATE_CREATIVE_DIMENSIONS,
  )
  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  setCreativeWidth(width) {
    return { width };
  },

  /**
   * Sets the creative mode
   *
   * @param creativeMode {String}
   * @returns {{creativeMode: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  setCreativeMode(creativeMode = CREATIVE_MODES.ADVANCED) {
    return { creativeMode };
  },

  /**
   * Sets the creative editor mode
   *
   * @param editorMode {String}
   * @returns {{editorMode: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  setEditorMode(editorMode) {
    return { editorMode };
  },

  /**
   * Sets the creative editor service mode
   *
   * @param editorServiceMode {String}
   * @returns {{editorServiceMode: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  setEditorServiceMode(editorServiceMode) {
    return { editorServiceMode };
  },

  /**
   * Sets the template to be imported.
   * When the user saves the creative it should be saved as a new Template
   *
   * @param templateToImport
   * @returns {{templateToImport: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  setTemplateToImport(templateToImport) {
    return { templateToImport };
  },

  /**
   * Appends the source video server id to the sourceVideoServerIds for the creative.
   *
   * @param serverId
   * @returns {{serverId: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  appendMetadataSourceVideoServerId(serverId) {
    return {
      serverId,
    };
  },

  /**
   * Sets out put ranges of creative if necessary
   *
   * @param outputRanges
   * @returns {outputRanges}
   */
  @actionCreator(REDUX_NAMESPACE)
  setMetadataOutputRanges(outputRanges) {
    return {
      outputRanges,
    };
  },

  /**
   * Load creative
   *
   * @param creativeId
   * @returns {{creativeId: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  loadCreative(creativeId) {
    return { creativeId };
  },

  /**
   * Reload the creative
   * skips resetting which clears the creative
   *
   * @param creativeId
   * @returns {{creativeId: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  reloadCreative(creativeId) {
    return {
      creativeId,
      isReload: true,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  unloadCreative() {},

  /**
   * Load creative with the saved creative_mode
   *
   * @param {String} creativeId
   * @param {boolean} openTrimmer - if true, video trimmer modal attempts to open automatically
   * @returns {{creativeId: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  loadCreativeWithCreativeMode(creativeId, openTrimmer) {
    return {
      creativeId,
      openTrimmer,
    };
  },

  /**
   * set creative ID that has been loaded
   *
   * @param loadedCreativeId
   * @returns {{loadedCreativeId: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  creativeLoaded(loadedCreativeId) {
    return { loadedCreativeId };
  },

  @actionCreator(REDUX_NAMESPACE)
  clearLoadedCreativeId() {},

  /**
   * sets the creativeOrigin
   *
   * @param creativeOrigin
   * @returns {{creativeOrigin: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  setCreativeOrigin(creativeOrigin) {
    return { creativeOrigin };
  },

  /**
   * sets the creativeStatus
   *
   * @param creativeStatus
   * @returns {{creativeStatus: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  setCreativeStatus(creativeStatus) {
    return { creativeStatus };
  },

  /**
   * sets isCreativeDesignSwapped
   *
   * @param isCreativeDesignSwapped
   * @returns {{isCreativeDesignSwapped: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  setIsCreativeDesignSwapped(isCreativeDesignSwapped = false) {
    return { isCreativeDesignSwapped };
  },

  @actionCreator(REDUX_NAMESPACE)
  doneButtonClicked() {},

  @actionCreator(REDUX_NAMESPACE)
  setOutputRangesForBuild(outputRangesForBuild) {
    return { outputRangesForBuild };
  },

  @actionCreator(REDUX_NAMESPACE)
  processOutputFramesForBuild() {},

  @actionCreator(REDUX_NAMESPACE)
  startProcessOutputFramesForBuild() {},

  @actionCreator(REDUX_NAMESPACE)
  finishProcessOutputFramesForBuild() {},

  @actionCreator(REDUX_NAMESPACE)
  setMlsMediaIdForPublish(mlsMediaIdForPublish) {
    return { mlsMediaIdForPublish };
  },

  @enqueueGAEvent(
    EVENT_CATEGORY.UI.NAME,
    EVENT_CATEGORY.UI.ACTION.DOWNLOAD_CURRENT_FRAME,
  )
  @actionCreator(REDUX_NAMESPACE)
  downloadCurrentFrame() {},
};

export default ACTION_CREATORS;
